import { useState, useEffect } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getPlansWithStatus } from '../../api/planApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';
const green = '#66ff99';
const orange = '#ffaa44';

const Slots = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    getPlansWithStatus()
      .then((res) => setPlans(res.data.plans))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load slots. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Container className="mt-5 text-center">
      <Spinner animation="border" style={{ color: cyan }} />
    </Container>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4">
        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Upgrade Slots</h5>
        <div style={{ color: '#888', fontSize: 13, marginBottom: 24 }}>
          Each slot can be purchased once. Slots unlock sequentially — complete each step to progress.
          After earning 1.5× the cumulative slot amount you may renew.
        </div>

        {error && (
          <div style={{ color: '#ff6666', background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,229,255,0.12)' }}>

          {/* Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '48px 1fr 160px 160px',
            background: 'rgba(0,229,255,0.07)',
            borderBottom: '1px solid rgba(0,229,255,0.15)',
            padding: '12px 20px',
          }}>
            {['#', 'Slot', 'Status', 'Renewal'].map((h) => (
              <div key={h} style={{ color: cyan, fontWeight: 600, fontSize: 13 }}>{h}</div>
            ))}
          </div>

          {/* Rows */}
          {plans.length === 0 ? (
            <div style={{ color: '#555', textAlign: 'center', padding: '32px', fontSize: 13 }}>
              No slots available
            </div>
          ) : (
            plans.map((plan, idx) => (
              <div
                key={plan._id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '48px 1fr 160px 160px',
                  padding: '14px 20px',
                  alignItems: 'center',
                  borderBottom: idx < plans.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                  background: plan.purchased
                    ? 'rgba(102,255,153,0.03)'
                    : plan.isEligible
                      ? idx % 2 === 0 ? 'rgba(0,229,255,0.02)' : 'transparent'
                      : 'transparent',
                  opacity: !plan.purchased && !plan.isEligible ? 0.55 : 1,
                }}
              >
                {/* # */}
                <div style={{ color: plan.purchased ? green : plan.isEligible ? cyan : '#444', fontSize: 13, fontWeight: 600 }}>
                  {idx + 1}
                </div>

                {/* Slot name + amount */}
                <div>
                  <div style={{ color: plan.purchased ? '#ccc' : plan.isEligible ? '#fff' : '#555', fontWeight: 600, fontSize: 14 }}>
                    {plan.planName}
                  </div>
                  <div style={{ color: plan.purchased ? green : plan.isEligible ? green : '#444', fontWeight: 700, fontSize: 13, marginTop: 2 }}>
                    {plan.amount} USDT
                  </div>
                  {plan.purchased && plan.currentRenewalCount > 0 && (
                    <div style={{ color: orange, fontSize: 10, marginTop: 2, fontWeight: 600 }}>
                      R{plan.currentRenewalCount} active
                    </div>
                  )}
                </div>

                {/* Primary purchase status */}
                <div>
                  {plan.purchased ? (
                    <div>
                      <span style={{
                        background: 'rgba(102,255,153,0.15)',
                        border: '1px solid rgba(102,255,153,0.3)',
                        color: green,
                        borderRadius: 6, padding: '4px 12px',
                        fontSize: 12, fontWeight: 600,
                      }}>
                        ✓ Upgraded
                      </span>
                      {plan.purchasedAt && (
                        <div style={{ color: '#555', fontSize: 11, marginTop: 4 }}>
                          {fmtIST(plan.purchasedAt)}
                        </div>
                      )}
                    </div>
                  ) : plan.isEligible ? (
                    <button
                      onClick={() => navigate(`/slots/buy/${plan._id}`)}
                      style={{
                        background: `linear-gradient(135deg, ${cyan} 0%, #00b8d9 100%)`,
                        border: 'none',
                        color: '#000',
                        borderRadius: 6,
                        padding: '5px 18px',
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Upgrade Now
                    </button>
                  ) : (
                    <div>
                      <span style={{ color: '#555', fontSize: 13 }}>🔒</span>
                      {plan.lockReason && (
                        <div style={{ color: '#484848', fontSize: 11, marginTop: 3 }}>
                          {plan.lockReason}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Renewal column */}
                <div>
                  {!plan.purchased ? (
                    <span style={{ color: '#333', fontSize: 12 }}>—</span>
                  ) : plan.renewalEligible ? (
                    <div>
                      <button
                        onClick={() => navigate(`/slots/renew/${plan._id}`)}
                        style={{
                          background: `linear-gradient(135deg, ${orange} 0%, #e08800 100%)`,
                          border: 'none',
                          color: '#000',
                          borderRadius: 6,
                          padding: '5px 14px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Renew (R{plan.nextRenewalCount})
                      </button>
                    </div>
                  ) : (
                    <div>
                      <span style={{ color: '#555', fontSize: 12 }}>🔒 R{plan.nextRenewalCount}</span>
                      {plan.renewalLockReason && (
                        <div style={{ color: '#444', fontSize: 10, marginTop: 3 }}>
                          {plan.renewalLockReason}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </Container>
    </div>
  );
};

export default Slots;
